import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { inputCls } from "../hooks/useMasterMaps";

export const MRF_STATUSES = ["Draft", "Pending Approval", "Approved", "In Sourcing", "In Selection",
  "Offer Stage", "On Hold", "Filled", "Rejected", "Cancelled", "Expired"];
export const MRF_TYPES = ["Replacement", "Additional Within Sanction", "Beyond Sanction", "Greenfield"];
export const MRF_PRIORITIES = ["Critical", "High", "Normal"];

const STATUS_CLS = {
  "Draft": "text-[#6B7280] border-gray-300 bg-gray-50",
  "Pending Approval": "text-[#D97706] border-[#D97706]/40 bg-[#D97706]/5",
  "Approved": "text-[#1B7F3B] border-[#1B7F3B]/40 bg-[#1B7F3B]/5",
  "In Sourcing": "text-[#1E5AA8] border-[#1E5AA8]/40 bg-[#1E5AA8]/5",
  "In Selection": "text-[#1E5AA8] border-[#1E5AA8]/40 bg-[#1E5AA8]/5",
  "Offer Stage": "text-[#1E5AA8] border-[#1E5AA8]/40 bg-[#1E5AA8]/5",
  "On Hold": "text-[#6B7280] border-gray-400 bg-gray-100",
  "Filled": "text-white border-[#1B7F3B] bg-[#1B7F3B]",
  "Rejected": "text-[#BB162B] border-[#BB162B]/40 bg-[#BB162B]/5",
  "Cancelled": "text-[#6B7280] border-gray-300 bg-gray-50",
  "Expired": "text-[#BB162B] border-[#BB162B]/40 bg-[#BB162B]/10",
};

export function StatusBadge({ status }) {
  return (
    <span data-testid="mrf-status-badge" className={`inline-block border px-2 py-0.5 text-[11px] font-medium rounded-sm whitespace-nowrap ${STATUS_CLS[status] || STATUS_CLS.Draft}`}>
      {status}
    </span>
  );
}

const SLA_CLS = {
  "On Track": "text-[#1B7F3B]",
  "At Risk": "text-[#D97706]",
  "Breached": "text-[#BB162B]",
  "On Hold": "text-[#6B7280]",
};

export function SlaChip({ sla }) {
  if (!sla) return null;
  return (
    <span data-testid="mrf-sla-chip" className={`inline-flex items-center gap-1.5 text-xs font-semibold ${SLA_CLS[sla] || ""}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current inline-block" /> {sla}
    </span>
  );
}

export function TypeBadge({ type, forced }) {
  const beyond = type === "Beyond Sanction";
  return (
    <span className={`inline-block border px-1.5 py-0.5 text-[10px] uppercase tracking-wide rounded-sm whitespace-nowrap ${
      beyond ? "text-[#BB162B] border-[#BB162B]/50 bg-[#BB162B]/5 font-semibold" : "text-[#6B7280] border-gray-300"}`}>
      {type}{forced && beyond ? " (auto)" : ""}
    </span>
  );
}

const OWNER_CLS = {
  BUSINESS: "bg-[#0E2240] text-white",
  HR: "bg-[#1E5AA8] text-white",
  CANDIDATE: "bg-[#BF9B30] text-white",
};

export function OwnerChip({ owner }) {
  return (
    <span className={`inline-block px-1.5 py-0.5 text-[10px] uppercase tracking-wide rounded-sm ${OWNER_CLS[owner] || "bg-gray-200 text-[#6B7280]"}`}>
      {owner}
    </span>
  );
}

export function DaysInStage({ days, sla }) {
  const color = sla === "Breached" ? "#BB162B" : sla === "At Risk" ? "#D97706" : "#1B7F3B";
  if (days == null) return <span className="text-xs text-[#6B7280]">—</span>;
  return <span className="text-xs font-semibold tabular-nums" style={{ color }}>{days}d</span>;
}

// Shared approve/reject/return/hold dialog
export function ActionDialog({ open, onOpenChange, action, reqNo, onConfirm, busy }) {
  const [remarks, setRemarks] = useState("");
  const [date, setDate] = useState("");
  const needsRemarks = action === "Reject" || action === "Return for Info";
  const needsDate = action === "Hold";
  const titles = {
    Approve: "Approve requisition", Reject: "Reject requisition",
    "Return for Info": "Return for more information", Hold: "Put requisition on hold",
  };
  const confirm = () => onConfirm(remarks, date).then(() => { setRemarks(""); setDate(""); });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white rounded-sm shadow-none border border-gray-200 max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif text-[#0E2240]">{titles[action] || action}</DialogTitle>
          <DialogDescription className="text-xs text-[#6B7280]">{reqNo}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          {needsDate && (
            <div>
              <label className="text-xs text-[#6B7280] block mb-1">Expected review date (mandatory)</label>
              <input data-testid="action-hold-date" type="date" className={inputCls} value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
          )}
          <textarea data-testid="action-remarks" className={`${inputCls} min-h-[80px]`}
            placeholder={needsRemarks ? "Reason (mandatory)" : "Remarks (optional)"}
            value={remarks} onChange={(e) => setRemarks(e.target.value)} />
        </div>
        <DialogFooter>
          <button onClick={() => onOpenChange(false)} className="px-4 py-2 text-sm border border-gray-300 rounded-sm hover:bg-gray-50 transition-colors duration-200">Cancel</button>
          <button data-testid="action-confirm" onClick={confirm}
            disabled={busy || (needsRemarks && !remarks.trim()) || (needsDate && !date)}
            className={`px-4 py-2 text-sm text-white rounded-sm transition-colors duration-200 disabled:opacity-50 ${
              action === "Reject" ? "bg-[#BB162B] hover:bg-[#a01324]" : action === "Approve" ? "bg-[#1B7F3B] hover:bg-[#166832]" : "bg-[#0E2240] hover:bg-[#1a3358]"}`}>
            {busy ? "Working…" : action}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
